import { UniqueIdentifier } from '@dnd-kit/core';

import { FieldId, FieldOption, FieldType, FormV2, fieldDefaultProps } from '@components/form/v2';
import { clamp } from '@utils/browser';
import { randomId } from '@utils/strings';

import { PredefinedOptionsLists } from './context';
import type {
  FormBuilderState,
  ProjectedTreePlacement,
  StateField,
  StateFieldMap,
  StateRootIds,
} from './state';

export const normalizeFields = (fields: Array<FieldType>) => {
  let fieldMap: StateFieldMap = {};
  let rootIds: StateRootIds = [];

  const normalizeField = (
    field: FieldType,
    fieldMap: StateFieldMap,
    level: number,
    parentId?: FieldId
  ): FieldId => {
    if (field.kind !== 'section') {
      fieldMap[field.id] = {
        ...fieldDefaultProps[field.kind],
        ...field,
        builder: {
          parentId,
          level,
        },
      };
      return field.id;
    }

    const newField: StateField = {
      ...fieldDefaultProps[field.kind],
      ...field,
      builder: {
        parentId,
        level,
      },
      fields: field.fields.map((child) => {
        // Add the children fields to the normalized state map
        return normalizeField(child, fieldMap, level + 1, field.id);
      }),
    };

    fieldMap[field.id] = newField;
    return field.id;
  };

  fields.forEach((field) => {
    const normalizedField = normalizeField(field, fieldMap, 0);
    rootIds.push(normalizedField);
  });

  return { fieldMap, rootIds };
};

export function isV2Form(form: any): form is FormV2 {
  if (!form) return false;
  if (Array.isArray(form)) return false;
  return form.schema === 'v2';
}

/**
 * Remove a field from the builder state list.
 * Note: Mutates the state in-place so requires calling with immer.
 */
export const removeFieldFromList = (fieldId: FieldId, state: FormBuilderState) => {
  const removedField = state.fieldMap[fieldId];
  if (removedField.kind === 'image' && removedField.imageId) {
    state.removedImages.push(removedField.imageId);
  }

  const removeFromParentFields = (parentId: FieldId, fieldToRemoveId: FieldId) => {
    const parentField = state.fieldMap[parentId];
    if (parentField && 'fields' in parentField) {
      const fieldIndex = parentField.fields.indexOf(fieldToRemoveId);
      if (fieldIndex !== -1) {
        parentField.fields.splice(fieldIndex, 1);
      }
    }
  };

  if (removedField.builder.parentId) {
    removeFromParentFields(removedField.builder.parentId, fieldId);
  } else {
    const rootFieldIndex = state.rootIds.indexOf(fieldId);
    if (rootFieldIndex !== -1) {
      state.rootIds.splice(rootFieldIndex, 1);
    }
  }
};

/**
 * When duplicating, we need to remove some props that dont apply
 * to the duplicated field, such as the builder props and
 * whether the field has already been assigned a attribute id
 */
const unsetPropsOnDuplicate = (field: StateField): StateField => {
  delete field.builderProps;
  if ('journeyAttributeId' in field) {
    delete field.journeyAttributeId;
  }
  if ('companyAttributeId' in field) {
    delete field.companyAttributeId;
  }
  return field;
};

/**
 * Duplicate a field in the builder state.
 * Note: Mutates the state in-place so requires calling with immer.
 */
export const duplicateFieldInState = (fieldId: FieldId, state: FormBuilderState): FieldId => {
  const duplicateNestedFields = (fields: Array<FieldId>, parentId?: FieldId) => {
    const duplicatedNestedFields: Array<FieldId> = [];

    fields.forEach((nestedFieldId) => {
      const clonedField = unsetPropsOnDuplicate({ ...state.fieldMap[nestedFieldId] });
      const duplicatedField = Object.assign(clonedField, {
        id: randomId(),
      });

      if (duplicatedField.builder) {
        duplicatedField.builder.parentId = parentId;
      }

      state.fieldMap[duplicatedField.id] = duplicatedField;

      if ('fields' in duplicatedField) {
        duplicatedField.fields = duplicateNestedFields(duplicatedField.fields, duplicatedField.id);
      }

      duplicatedNestedFields.push(duplicatedField.id);
    });

    return duplicatedNestedFields;
  };

  const clonedField = unsetPropsOnDuplicate({
    ...state.fieldMap[fieldId],
    id: randomId(),
    builderProps: undefined,
  });
  state.fieldMap[clonedField.id] = clonedField;

  if ('fields' in clonedField) {
    clonedField.fields = duplicateNestedFields(clonedField.fields, clonedField.id);
  }

  if (clonedField.builder.parentId) {
    const parentField = state.fieldMap[clonedField.builder.parentId];
    if (parentField && 'fields' in parentField) {
      const duplicatedFieldIndex = parentField.fields.indexOf(fieldId);
      parentField.fields.splice(duplicatedFieldIndex + 1, 0, clonedField.id);
    }
  } else {
    const rootFieldIndex = state.rootIds.indexOf(fieldId);
    state.rootIds.splice(rootFieldIndex + 1, 0, clonedField.id);
  }

  return clonedField.id;
};

export const deserializeState = (state: FormBuilderState) => {
  const deserializeField = (fieldId: FieldId): FieldType => {
    const field = state.fieldMap[fieldId];

    if ('fields' in field) {
      const { builder, ...rest } = field;
      return {
        ...rest,
        fields: field.fields.map(deserializeField),
      };
    }

    const { builder, ...rest } = field;
    return { ...rest };
  };

  return state.rootIds.map(deserializeField);
};

export const getProjectedPlacement = ({
  fields,
  overId,
  activeId,
  offset,
  expanded,
}: {
  fields: Array<StateField>;
  overId: UniqueIdentifier | null;
  activeId: UniqueIdentifier | null;
  offset: number;
  expanded: Array<string>;
}): ProjectedTreePlacement => {
  if (!overId || !activeId) return null;

  const activeIndex = fields.findIndex((field) => field.id === activeId);
  const overIndex = fields.findIndex((field) => field.id === overId);
  const activeField = fields[activeIndex];
  const overField = fields[overIndex];
  const isMovingDown = overIndex > activeIndex;

  let projectedParentId: string | undefined = undefined;
  let maxDepth = 0;
  let minDepth = 0;

  /**
   * If the active item is the first item after an open section,
   * dragging the item straight to the right should allow placing it inside
   * the open section before it.
   *
   * Since this case (especially the else branch) can really work for
   * any node anywhere there's an open section above it (f.ex. the children of that section),
   * we need to do this first. The cases that follow can then refine the
   * depth and the parent if those more narrow cases are met.
   */
  const itemBeforeMe = fields.at(activeIndex - 1);
  if (
    itemBeforeMe?.kind === 'section' &&
    itemBeforeMe.id !== activeField.builder.parentId &&
    activeId === overId &&
    expanded.includes(itemBeforeMe.id)
  ) {
    maxDepth = 1;
    projectedParentId = itemBeforeMe.id;
  } else {
    if (itemBeforeMe?.builder.parentId && expanded.includes(itemBeforeMe.builder.parentId)) {
      maxDepth = clamp(activeField.builder.level, offset, itemBeforeMe.builder.level);
      projectedParentId = itemBeforeMe.builder.parentId;
    }
  }

  const overParent = overField.builder.parentId
    ? fields.find((field) => field.id === overField.builder.parentId)
    : undefined;

  if (overParent?.kind === 'section' && expanded.includes(overParent.id)) {
    const isOverOpenSection = overField.kind === 'section' && expanded.includes(overField.id);
    minDepth = 0;
    maxDepth = isOverOpenSection && overId !== activeId ? 1 : 0;
    projectedParentId = overParent.id;

    // Currently hovering the last item in the section fields.
    // Need to allow dragging it left to make it go
    // outside the section and place right after it.
    const lastId = overParent.fields[overParent.fields.length - 1];
    if (lastId === overId) {
      minDepth = -1;
      const depth = clamp(minDepth, offset, maxDepth);
      if (depth === -1) projectedParentId = overParent.builder.parentId;
    }
  }

  // Hovering over an open section
  // AND if moving down: on the first slot inside.
  // OR if moving up: on the first slot above outside.
  if (overField.kind === 'section' && expanded.includes(overField.id)) {
    maxDepth = isMovingDown ? 1 : 0;

    const hasNoFields = overField.fields.length === 0;
    if (hasNoFields) {
      minDepth = 0;
      maxDepth = isMovingDown ? 1 : 0;
    } else {
      minDepth = isMovingDown ? 1 : 0;
    }

    const depth = clamp(minDepth, offset, maxDepth);
    projectedParentId = overParent?.id;

    if (isMovingDown && depth === 1) {
      projectedParentId = overField.id;
    }
  }

  return {
    level: clamp(minDepth, offset, maxDepth),
    parentId: projectedParentId,
  };
};

export const mergePredefinedOptions = (
  fields: FormV2['fields'],
  predefinedLists: PredefinedOptionsLists
): FormV2['fields'] => {
  const mergeOptions = (field: FieldType): FieldType => {
    if (field.kind === 'section') {
      return {
        ...field,
        fields: field.fields.map(mergeOptions),
      };
    }
    if ('options' in field && field.optionsFromListId) {
      const allOptions =
        predefinedLists.find((opt) => opt.value === field.optionsFromListId)?.options ?? [];
      const excludedOptions = field.excludedOptions ?? [];
      const options = filterOutExcludedOptions(allOptions, excludedOptions);
      return {
        ...field,
        options,
      };
    }
    return field;
  };

  return fields.map(mergeOptions);
};

export const filterOutExcludedOptions = (options: FieldOption[], excludedOptions: string[]) => {
  return options.filter((option) => !excludedOptions.includes(option.value));
};
